// Leagues the current user belongs to, sorted by name. Shared by Home.js and
// the league switcher sheet so both list the same set the same way.
import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabase/supabase';
import { useAuth } from '../context/AuthContext';
import { getDisplayData, getBestWeekStats, getCurrentWeekNumber } from '../utils/leagueUtils';
import { SEASON_YEAR } from '../utils/season';
import { buildLeagueCard, kickoffFull, MOVES_PER_LEAGUE, MOVES_WINDOW_MS } from '../utils/leagueCard';
import {
  anyGameSoon,
  buildGamesBySlug,
  getGameDisplayInfo,
  getGameSpreadDisplay,
  shouldShowSpread,
  POLL_MS,
} from '../utils/gameStatus';

// avatar_url and the users embed are for the league card, which pictures and
// names managers. last_name is only for a last initial when two managers in a
// league share a first name. Same embed shape LeagueContext already reads.
const MEMBER_COLUMNS = 'id, league_id, user_id, points, team_name, avatar_url, users(first_name, last_name)';
// starters is for the viewer's own lineup strip on the card.
const LINEUP_COLUMNS = 'member_id, week, points, starters, captain, trip_play_team, team_points';
const GAME_COLUMNS = 'home_team, away_team, home_score, away_score, home_spread, period, clock, game_status, game_complete, date';

// demo: the signed-out /demo Home. The list is the public demo league
// (migration 078), seen as its admin, matching the "view as" LeagueContext
// uses once the visitor opens it.
//
// live: refetch scores while games are on. Only Home asks for it; the league
// switcher sheet lists names and has no use for live points.
export function useUserLeagues({ demo = false, live = false } = {}) {
  const { currentUser, seasonConfig, teams } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  // Everything the last load or poll read, so a poll can refetch only what
  // changes during a game and rebuild the rest from here.
  const cacheRef = useRef(null);

  const currentWeekNum = getCurrentWeekNumber(seasonConfig?.currentWeek);

  const load = useCallback(async () => {
    let viewerId;
    let leagueIds;

    if (demo) {
      const { data: demoLeague } = await supabase
        .from('leagues').select('id, admin_id').eq('is_demo', true).limit(1).maybeSingle();
      if (!demoLeague) { cacheRef.current = null; setLeagues([]); setLoading(false); return; }
      viewerId = demoLeague.admin_id;
      leagueIds = [demoLeague.id];
    } else {
      if (!currentUser) { cacheRef.current = null; setLeagues([]); setLoading(false); return; }
      viewerId = currentUser.id;

      // Deliberately two queries, not an embedded select on leagues. The embed
      // returned no rows here: league_members_read is is_league_member(league_id),
      // which the embed evaluates differently from a plain read, so every user
      // saw an empty league list.
      const { data: memberRows } = await supabase
        .from('league_members').select('league_id').eq('user_id', currentUser.id);

      leagueIds = (memberRows || []).map((r) => r.league_id);
    }
    if (leagueIds.length === 0) { cacheRef.current = null; setLeagues([]); setLoading(false); return; }

    // draft_complete rides along here: /home needs it to decide which actions a
    // league card offers, and it is the same column LeagueContext derives
    // isDraftComplete from.
    //
    // Several small reads rather than one: /home says what each league is up
    // to, which needs the draft's state and the league's members. All are keyed
    // by the same handful of league ids and return a few dozen rows in total.
    //
    // weekly_lineups is not filtered to the current week: the standings
    // tiebreak (getDisplayData) needs each member's best week across the whole
    // season. That is the one heavy read, and it happens once per visit; the
    // poll below refetches the current week only.
    //
    // This week's games are always read, not only when a 5x Play is live:
    // they are what tells the poll whether anything is on.
    //
    // Recent roster moves feed the ticker. One read across every league,
    // newest first, capped at a few per league; the manager and school names
    // come from rows already loaded, so there is no embed (move_history_read
    // is a helper-function policy, the kind an embed silently empties).
    const [{ data: leaguesData }, { data: draftRows }, { data: allMemberRows }, { data: lineupRows }, games, { data: moveRows }] =
      await Promise.all([
        supabase.from('leagues').select('id, name, draft_complete').in('id', leagueIds),
        supabase.from('drafts').select('league_id, status, current_pick, total_rounds').in('league_id', leagueIds),
        supabase.from('league_members').select(MEMBER_COLUMNS).in('league_id', leagueIds),
        supabase.from('weekly_lineups').select(LINEUP_COLUMNS).in('league_id', leagueIds),
        fetchWeekGames(currentWeekNum),
        supabase.from('move_history').select('league_id, member_id, team_added, team_dropped, created_at')
          .in('league_id', leagueIds)
          .gte('created_at', new Date(Date.now() - MOVES_WINDOW_MS).toISOString())
          .order('created_at', { ascending: false })
          .limit(MOVES_PER_LEAGUE * leagueIds.length),
      ]);

    const cache = {
      viewerId,
      leagueIds,
      leaguesData: leaguesData || [],
      draftRows: draftRows || [],
      memberRows: allMemberRows || [],
      lineupRows: lineupRows || [],
      games,
      // Not refetched by the poll: teams lock at kickoff, so rosters barely
      // move while games are on, and the next visit picks up anything new.
      moveRows: moveRows || [],
      week: currentWeekNum,
    };
    cacheRef.current = cache;
    setLeagues(assembleLeagues(cache, teams));
    setLoading(false);
  }, [demo, currentUser, currentWeekNum, teams]);

  useEffect(() => { load(); }, [load]);

  // While a game is running or about to, refetch what a game changes: every
  // member's season total, this week's lineup rows and this week's games.
  // Earlier weeks only feed the tiebreak and do not change mid-week, so they
  // stay as loaded. Same cadence and gate as Pick Three and the lineup tab:
  // POLL_MS matches the ESPN ingestion cron, and anyGameSoon keeps a quiet
  // week to the single load above. Nothing runs while the tab is hidden, and
  // coming back to it checks straight away.
  //
  // league_members.points and the week's weekly_lineups row are written in
  // the same recalculation pass, so a poll cannot see one updated without
  // the other.
  useEffect(() => {
    if (!live || loading) return undefined;
    let cancelled = false;
    const tick = async () => {
      const cache = cacheRef.current;
      if (!cache || document.visibilityState === 'hidden' || !anyGameSoon(cache.games)) return;
      const [members, weekRows, games] = await Promise.all([
        supabase.from('league_members').select(MEMBER_COLUMNS).in('league_id', cache.leagueIds),
        supabase.from('weekly_lineups').select(LINEUP_COLUMNS).in('league_id', cache.leagueIds).eq('week', cache.week),
        fetchWeekGames(cache.week),
      ]);
      // A failed read keeps what is on screen rather than blanking it.
      if (cancelled || cacheRef.current !== cache || members.error || weekRows.error) return;
      const next = {
        ...cache,
        memberRows: members.data || [],
        lineupRows: [
          ...cache.lineupRows.filter((row) => row.week !== cache.week),
          ...(weekRows.data || []),
        ],
        games: games.length ? games : cache.games,
      };
      cacheRef.current = next;
      setLeagues(assembleLeagues(next, teams));
    };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [live, loading, teams]);

  return { leagues, loading, refresh: load };
}

async function fetchWeekGames(week) {
  const { data, error } = await supabase
    .from('games').select(GAME_COLUMNS).eq('year', SEASON_YEAR).eq('week', week);
  return error ? [] : data || [];
}

// Everything the cards show, from what was read. Pure, so a load and a poll
// build the list the same way.
export function assembleLeagues(cache, teams) {
  const { viewerId, leaguesData, draftRows, memberRows, lineupRows, games, week, moveRows = [] } = cache;

  const draftByLeague = {};
  draftRows.forEach((d) => { draftByLeague[d.league_id] = d; });

  const memberById = {};
  memberRows.forEach((m) => { memberById[m.id] = m; });

  // Rows with an active 5x Play this week, per league. A manager who
  // already spent it in an earlier week has a trip_play_team row too, just
  // not for this week, so the week check is what keeps a used-and-done
  // play out of a card that means "live right now."
  const tripPlayRowsByLeague = {};
  lineupRows.forEach((row) => {
    if (row.week !== week || !row.trip_play_team) return;
    const member = memberById[row.member_id];
    if (!member) return;
    (tripPlayRowsByLeague[member.league_id] = tripPlayRowsByLeague[member.league_id] || [])
      .push({ member, slug: row.trip_play_team });
  });

  // The same shape WeeklyLineupManager reads for the Lineup tab, via
  // buildGamesBySlug, so the score on this card can never disagree with
  // what the tab itself shows for that team.
  const gamesBySlug = buildGamesBySlug(games);

  const tripPlayInPlayByLeague = {};
  Object.entries(tripPlayRowsByLeague).forEach(([leagueId, rows]) => {
    tripPlayInPlayByLeague[leagueId] = rows.map(({ member, slug }) => {
      const gameInfo = gamesBySlug[slug] || null;
      const displayInfo = getGameDisplayInfo(gameInfo, slug, teams);
      const spread = shouldShowSpread(displayInfo) ? getGameSpreadDisplay(gameInfo, slug) : null;
      const gameLine = displayInfo.type === 'bye-week'
        ? 'Bye week'
        : [displayInfo.subtext, displayInfo.display, spread ? `Spread ${spread}` : null]
            .filter(Boolean).join(' · ');
      return {
        memberId: member.id,
        teamName: member.team_name || 'Unnamed Team',
        firstName: member.users?.first_name || null,
        avatarUrl: member.avatar_url || null,
        ncaaSchool: teams?.[slug]?.school || slug,
        gameLine,
        // Just the score and clock, for the league card's ticker. Nothing
        // before kickoff: a date there would read as a result.
        score: displayInfo.type === 'live' || displayInfo.type === 'completed' ? displayInfo.display : null,
        // And the kickoff, before it.
        kickoff: displayInfo.type === 'upcoming' && gameInfo?.date ? kickoffFull(gameInfo.date) : null,
      };
    });
  });

  const seasonRowsByMemberId = {};
  const lineupRowsByLeague = {};
  lineupRows.forEach((row) => {
    (seasonRowsByMemberId[row.member_id] = seasonRowsByMemberId[row.member_id] || []).push(row);
    const member = memberById[row.member_id];
    if (member) (lineupRowsByLeague[member.league_id] = lineupRowsByLeague[member.league_id] || []).push(row);
  });

  // Standing is derived here rather than read: the rows are already loaded,
  // and weekly_standings holds nothing until a week has been scored.
  const membersByLeague = {};
  const membersRawByLeague = {};
  memberRows.forEach((m) => {
    const { bestWeekPoints, bestWeekCaptainPoints } = getBestWeekStats(seasonRowsByMemberId[m.id]);
    (membersByLeague[m.league_id] = membersByLeague[m.league_id] || []).push({
      ...m,
      bestWeekPoints,
      bestWeekCaptainPoints,
      teamName: m.team_name || 'Unnamed Team',
    });
    (membersRawByLeague[m.league_id] = membersRawByLeague[m.league_id] || []).push(m);
  });

  const standingByLeague = {};
  Object.entries(membersByLeague).forEach(([id, rows]) => {
    const sorted = getDisplayData({ viewMode: 'current', members: rows });
    const index = sorted.findIndex((m) => m.user_id === viewerId);
    standingByLeague[id] = {
      memberCount: rows.length,
      rank: index === -1 ? null : index + 1,
      points: index === -1 ? null : (sorted[index].points || 0),
      teamName: index === -1 ? null : sorted[index].teamName,
    };
  });

  // The live table and ticker on each card. Built from the same rows as the
  // standing above, so the two cannot disagree about who is where.
  const school = (slug) => (slug ? teams?.[slug]?.school || slug : null);
  const movesByLeague = {};
  moveRows.forEach((m) => {
    (movesByLeague[m.league_id] = movesByLeague[m.league_id] || []).push({
      member_id: m.member_id,
      added: school(m.team_added),
      dropped: school(m.team_dropped),
      created_at: m.created_at,
    });
  });

  const cardByLeague = {};
  Object.entries(membersRawByLeague).forEach(([id, rows]) => {
    const tripPlayByMember = {};
    (tripPlayInPlayByLeague[id] || []).forEach((entry) => {
      tripPlayByMember[entry.memberId] = { school: entry.ncaaSchool, score: entry.score, kickoff: entry.kickoff };
    });
    cardByLeague[id] = buildLeagueCard({
      members: rows,
      lineupRows: lineupRowsByLeague[id] || [],
      viewerId,
      week,
      tripPlayByMember,
      moves: movesByLeague[id] || [],
      gamesBySlug,
      teams,
    });
  });

  return leaguesData
    .map((l) => ({
      ...l,
      draft: draftByLeague[l.id] || null,
      standing: standingByLeague[l.id] || { memberCount: 0, rank: null, points: null },
      tripPlayInPlay: tripPlayInPlayByLeague[l.id] || [],
      card: cardByLeague[l.id] || null,
    }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}
